import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Tooltip } from '@repo/design-system/components/ds/tooltip/tooltip';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { useCurrentLanguage } from '@repo/i18n/hooks/use-current-language';
import { css } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectCurrentMapInfo, selectExternalMapId, selectSteamId, selectTitleName } from '@repo/store/maps/slices/mapData';
import type { GameSessionInternal } from '@repo/types/sessions/session-types';
import { getSteamImageUrl } from '@repo/utility/image-urls';
import { formatDurationHumanReadable } from '@repo/utility/time';
import React, { useEffect } from 'react';
import { useSessionData } from '../game-map/hooks/useSessionData';
import { useSessionHistory } from '../game-map/hooks/useSessionHistory';
import { dividerStyles, heroSectionStyles } from '../utils/sidebar-styles';
import { SessionShareMenu } from './session-share-menu';
interface SessionHistoryViewProps {
  onBack: () => void;
  onSessionSelect?: (session: GameSessionInternal) => void;
  isMobilePane?: boolean;
}
export const SessionHistoryView: React.FC<SessionHistoryViewProps> = ({
  onBack,
  onSessionSelect,
  isMobilePane = false
}) => {
  const {
    t
  } = useT(['game_map']);
  const language = useCurrentLanguage();
  const currentMap = useAppSelector(selectCurrentMapInfo);
  const titleName = useAppSelector(selectTitleName) || t($ => $.unknown_game);
  const steamId = useAppSelector(selectSteamId);
  const externalMapId = useAppSelector(selectExternalMapId);
  const isLoggedIn = useAppSelector(state => state.auth.isAuthenticated);
  const analytics = useAnalytics();
  const hostType = useHostType();
  const {
    sessions,
    loading,
    loadingMore,
    fetchSessionDetails,
    hasMore,
    loadMore,
    updateSessionSharing
  } = useSessionData({
    titleId: Number(currentMap?.titleId ?? '0'),
    autoFetch: isLoggedIn
  });
  const {
    hideSessionTrail,
    setTimelineVisible,
    currentSession,
    setCurrentSession
  } = useSessionHistory();
  const [fetchingSessionUuid, setFetchingSessionUuid] = React.useState<string | null>(null);
  const fetchCancellationRef = React.useRef(false);
  const sessionDetailsCache = React.useRef<Map<string, GameSessionInternal>>(new Map());
  const [hoveredSessionUuid, setHoveredSessionUuid] = React.useState<string | null>(null);
  const handleSharingChange = React.useCallback((sessionUuid: string, isShared: boolean) => {
    updateSessionSharing(sessionUuid, isShared);
    const cached = sessionDetailsCache.current.get(sessionUuid);
    if (cached) {
      sessionDetailsCache.current.set(sessionUuid, {
        ...cached,
        isShared
      });
    }
  }, [updateSessionSharing]);
  const selectSession = React.useCallback((session: GameSessionInternal) => {
    setCurrentSession(session);
    setTimelineVisible(true);
    onSessionSelect?.(session);
    analytics.event('game_map_timeline_session_selected', {
      mapId: externalMapId,
      sessionUuid: session.uuid,
      mapName: currentMap?.name,
      titleName,
      platform: hostType
    });
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('sessionUuid', session.uuid);
      url.searchParams.set('sessionStartedAt', session.startedAt);
      url.searchParams.set('sessionCompletedAt', session.completedAt || new Date().toISOString());
      window.history.replaceState({}, '', url.toString());
    }
  }, [setCurrentSession, setTimelineVisible, onSessionSelect, analytics, externalMapId, currentMap?.name, titleName, hostType]);
  const loadSessionDetails = React.useCallback(async (session_0: GameSessionInternal): Promise<GameSessionInternal | null> => {
    const cached_0 = sessionDetailsCache.current.get(session_0.uuid);
    if (cached_0) return cached_0;
    if (fetchingSessionUuid) return null;
    setFetchingSessionUuid(session_0.uuid);
    fetchCancellationRef.current = false;
    try {
      const endTime = session_0.completedAt || new Date().toISOString();
      const sessionWithDetails = await fetchSessionDetails(session_0.uuid, session_0.startedAt, endTime);
      if (fetchCancellationRef.current) return null;
      if (sessionWithDetails) {
        sessionDetailsCache.current.set(session_0.uuid, sessionWithDetails);
      }
      return sessionWithDetails;
    } finally {
      if (!fetchCancellationRef.current) {
        setFetchingSessionUuid(null);
      }
    }
  }, [fetchSessionDetails, fetchingSessionUuid]);
  const handleSessionClick = React.useCallback(async (session_1: GameSessionInternal) => {
    const sessionWithDetails = await loadSessionDetails(session_1);
    if (!sessionWithDetails) return;
    if (sessionWithDetails.trail && sessionWithDetails.trail.length > 0) {
      selectSession(sessionWithDetails);
    } else {
      toast({
        description: t($_0 => $_0.session_history.no_location_data),
        variant: 'error'
      });
    }
  }, [selectSession, loadSessionDetails, t]);
  useEffect(() => {
    return () => {
      fetchCancellationRef.current = true;
      hideSessionTrail();
      setTimelineVisible(false);
      setCurrentSession(null);
      if (typeof window !== 'undefined') {
        const url_0 = new URL(window.location.href);
        url_0.searchParams.delete('sessionUuid');
        url_0.searchParams.delete('sessionStartedAt');
        url_0.searchParams.delete('sessionCompletedAt');
        window.history.replaceState({}, '', url_0.toString());
      }
    };
  }, [hideSessionTrail, setTimelineVisible, setCurrentSession]);
  const heroImageUrl = steamId ? getSteamImageUrl(steamId, 'hero') : '';
  const humanReadableSessionDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleDateString(language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  };
  return <FlexColumn gap="12" width="full" height="full" className={heroSectionStyles} style={{
    backgroundImage: `url(${heroImageUrl})`
  }}>
            <FlexColumn gap="24" className={css.raw({
      paddingTop: isMobilePane ? '24' : '0'
    })}>
                <Button variant="filled" onClick={onBack} color="secondary" className={css({
        backdropFilter: '[blur(10px)]'
      })}>
                    <Icon icon="arrow_back" decorative />
                    {t($_1 => $_1.session_history.back)}
                </Button>
                <FlexRow gap="12" align="center" width="full">
                    <FlexColumn gap="4">
                        <Type variant="header-m" color="white">
                            {t($_2 => $_2.session_history.your_timeline)}
                        </Type>
                        <Type variant="body-s" color="text.primary">
                            {t($_3 => $_3.session_history.go_back_in_time, {
              titleName
            })}
                        </Type>
                    </FlexColumn>
                </FlexRow>
            </FlexColumn>

            <hr className={dividerStyles} />

            <FlexColumn gap="12" width="full" className={css.raw({
      flex: '1',
      minHeight: '0',
      overflowY: 'auto'
    })}>
                <Type variant="caption" color="white">
                    {t($_4 => $_4.session_history.all_sessions)}
                </Type>
                {loading ? <FlexColumn width="full" align="center">
                        <Type variant="body-s" color="text.secondary">
                            {t($_5 => $_5.session_history.loading_sessions)}
                        </Type>
                    </FlexColumn> : sessions.length === 0 ? <Type variant="body-s" color="text.secondary">
                        {t($_6 => $_6.session_history.no_sessions_found, {
          titleName
        })}
                    </Type> : <FlexColumn gap="6" width="full">
                        {sessions.map(session_2 => {
          const isSelected = currentSession?.uuid === session_2.uuid;
          const isLoading = fetchingSessionUuid === session_2.uuid;
          const isAnySessionLoading = fetchingSessionUuid !== null;
          const isDisabled = session_2.hasPositions === false || isAnySessionLoading || loading;
          return <Tooltip key={session_2.uuid} content={t($_7 => $_7.session_history.no_location_data_tooltip)} disabled={session_2.hasPositions !== false} placement="top">
                                    {/* biome-ignore lint/a11y/noStaticElementInteractions: handled for share button trigger */}
                                    <div className={css({
              position: 'relative',
              width: 'full'
            })} onMouseEnter={() => setHoveredSessionUuid(session_2.uuid)} onMouseLeave={() => setHoveredSessionUuid(null)} onFocus={() => setHoveredSessionUuid(session_2.uuid)} onBlur={() => setHoveredSessionUuid(null)}>
                                        <Button variant={isSelected ? 'filled' : 'ghost'} onClick={() => handleSessionClick(session_2)} color="secondary" size="md" radius="rounded" disabled={isDisabled} className={css({
                width: 'full',
                justifyContent: 'start',
                paddingX: '12',
                paddingRight: hoveredSessionUuid === session_2.uuid ? '[44px]' : '12'
              })}>
                                            {isLoading ? <Icon icon="hourglass_empty" size="sm" decorative /> : <Icon icon="check" size="sm" className={css({
                  visibility: isSelected && !isLoading ? 'visible' : 'hidden'
                })} decorative />}
                                            <Type variant="body-s" truncate title={isLoading ? undefined : humanReadableSessionDate(session_2.startedAt)}>
                                                {isLoading ? t($_8 => $_8.session_history.loading) : humanReadableSessionDate(session_2.startedAt)}
                                            </Type>
                                            {session_2.isShared && <span className={css({
                  width: '[8px]',
                  height: '[8px]',
                  borderRadius: 'full',
                  backgroundColor: 'success',
                  flexShrink: 0
                })} />}
                                            <Type variant="body-s" className={css({
                  marginLeft: 'auto'
                })}>
                                                {formatDurationHumanReadable(session_2.duration)}
                                            </Type>
                                        </Button>
                                        {session_2.hasFirestoreData !== false && (/* biome-ignore lint/a11y/noStaticElementInteractions: wrapper prevents row click from firing when interacting with share menu */
              /* biome-ignore lint/a11y/useKeyWithClickEvents: click handler only prevents propagation */
              <div className={css({
                position: 'absolute',
                right: '4',
                top: '[50%]',
                transform: 'translateY(-50%)',
                opacity: hoveredSessionUuid === session_2.uuid ? 1 : 0,
                pointerEvents: hoveredSessionUuid === session_2.uuid ? 'auto' : 'none'
              })} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                                <SessionShareMenu session={session_2} onSharingChange={handleSharingChange} disabled={isAnySessionLoading} placement="bottom-end" trigger="map-session-sidebar" />
                                            </div>)}
                                    </div>
                                </Tooltip>;
        })}
                        {hasMore && <Button variant="ghost" onClick={loadMore} color="secondary" disabled={loadingMore} className={css({
          width: 'full'
        })}>
                                {t($_9 => $_9.session_history.load_more_sessions)}
                            </Button>}
                    </FlexColumn>}
            </FlexColumn>
        </FlexColumn>;
};